import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { AgGridFilterModelInterface, Filter, getFilterValues } from '../extended-table.component';
import { MatButtonModule } from '@angular/material/button';
import { subscribeInComponent } from "src/app/shared/angular-utils";
import { ToolbarEventsService } from 'src/app/core';
import { TranslateModule } from '@ngx-translate/core';

export type QuickFiltersInputOperation = 'reset' | 'resetOnlyFilters' | 'initFilters' | ''
export type QuickFiltersOutputOperation = 'applyFilters' | 'toggleSidenav' | 'reset' | 'resetFilteredResult' | ''

export interface ParamsInputOperationsQuickFilter {
  operation: QuickFiltersInputOperation,
  extras?: any
}

export interface ParamsOutputOperations {
  operation: QuickFiltersOutputOperation,
  extras?: any
}

@Component({
  selector: 'app-quick-filters',
  standalone: true,
  imports: [MatFormFieldModule,
    MatIconModule, CommonModule, 
    MatCheckboxModule, ReactiveFormsModule,
    MatButtonModule, TranslateModule
  ],
  templateUrl: './quick-filters.component.html',
  styleUrls: ['./quick-filters.component.scss', '../extended-table.component.scss']
})
export class QuickFiltersComponent implements OnInit {

  private readonly fb = inject(FormBuilder);
  private readonly toolbarEventsService = inject(ToolbarEventsService);
  

  private _inputOperation: QuickFiltersInputOperation = '';
  @Input()
  set triggerOperation(value: ParamsInputOperationsQuickFilter) {
    this._inputOperation = value.operation;
    if(value.operation === 'reset') {
      this.resetQuickFilter(value.extras?.deepReset);
    } else if(value.operation === 'resetOnlyFilters') {
      this.resetOnlyQuickFilter();
    } else if(value.operation === 'initFilters') {
      this.initFilterValues(value.extras?.filterStatus);
    }
  }
  get inputOperation(): QuickFiltersInputOperation  {
    return this._inputOperation;
  }

  @Input() filterApplied: boolean = false;
  @Input() hideResetFilterIcon: boolean = false;
  @Input() quickFilters: Filter[] = [];
  @Input() filterStatusSaved: AgGridFilterModelInterface | null = null;

  @Output() outputOperation = new EventEmitter<ParamsOutputOperations>();

  quickFiltersForm: FormGroup;

  constructor() {
    this.quickFiltersForm = this.fb.group({});

    subscribeInComponent(this.toolbarEventsService.events, (event) => {
      switch(event.type) {
        case 'toolbar.clear_external_filter':
          return this.resetQuickFilter(true);
        default:
          break;
      }
    });
  }

  formReady = false;

  ngOnInit(): void {
    this.buildQuickFilterForm();
    this.initFilterValues(this.filterStatusSaved);
    this.formReady = true;
  }

  private buildQuickFilterForm(): void {
    this.quickFilters.forEach(filter => {
      const filterGroup = this.fb.group({
        options: this.fb.array(filter.options.map((option) => {
          return this.fb.group({
            id: [option.id || null],
            value: [option.name || null],
            selected: [false]
          });
        }))
      });
      this.quickFiltersForm.setControl(filter.field, filterGroup);
    });
  }

  private initFilterValues(filterStatus: AgGridFilterModelInterface | null): void {
    if (!filterStatus) return;

    this.quickFilters.forEach(filter => {
      const savedValues: any[] = getFilterValues(filterStatus, filter.field) || [];
      const filterGroup = this.quickFiltersForm.get(filter.field) as FormGroup;
      
      if (filterGroup) {
        const optionsArray = filterGroup.get('options') as FormArray;
        
        optionsArray.controls.forEach((control) => {
          const id = control.get('id')?.value;
          const isSelected = savedValues.some(v => String(v) === String(id));
          control.get('selected')?.patchValue(isSelected, { emitEvent: false });
        });
      }
    });
  }
  
  getOptionsQuickFilterFormArray(filterName: string): FormArray {
    return this.quickFiltersForm.get([filterName, 'options']) as FormArray;
  }

  getOptionQuickFilterFormGroup(filterName: string, index: number): FormGroup {
    return (this.quickFiltersForm.get([filterName, 'options']) as FormArray).at(index) as FormGroup;
  }
  
  checkboxQuickFilterSelectionChange($event: MatCheckboxChange) {
    this.outputOperation.emit({operation: 'applyFilters', extras: {formValue: this.quickFiltersForm.value}})
  }
  
  resetQuickFilter(deepReset = false): void {
    this.resetOnlyQuickFilter();
    this.outputOperation.emit({operation: "reset", extras: {deepReset: deepReset}})
  }

  resetOnlyQuickFilter(): void {
    if(this.quickFiltersForm?.value && Object.keys(this.quickFiltersForm.value).length) {
      for(let filterName in this.quickFiltersForm.value) {
        this.getOptionsQuickFilterFormArray(filterName)?.controls.forEach((optionFilterGroup) => {
          if(optionFilterGroup.get('selected')?.value) {
            optionFilterGroup.get('selected')?.setValue(false, {emitEvent: false});
          }
        });
      }
    }
  }

  toggleSidenav() {
    this.outputOperation.emit({operation: "toggleSidenav"})
  }
}
